import React from "react";
import { motion } from "framer-motion";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Globe, ChevronDown } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { SITE } from "../lib/site";

const NavLink = ({ href, children, testId }) => (
  <a
    href={href}
    data-testid={testId}
    className="text-sm text-neutral-400 hover:text-yellow-400 transition-colors font-mono-tech tracking-wide uppercase"
  >
    {children}
  </a>
);

export default function Header() {
  const { lang, setLang, t } = useLanguage();

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <a
            href="#top"
            data-testid="logo-link"
            className="flex items-center gap-3 group"
          >
            <span className="inline-flex h-8 w-8 items-center justify-center bg-yellow-500 text-black font-black font-display">
              L
            </span>
            <div className="flex flex-col leading-tight">
              <span className="text-xs font-mono-tech text-neutral-400 uppercase tracking-[0.2em]">
                ETJ
              </span>
              <span className="text-sm font-display font-semibold text-white">
                {SITE.shortName.split(" · ")[1] || SITE.shortName}
              </span>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-7">
            <NavLink href="#about" testId="nav-about">
              {t.nav.about}
            </NavLink>
            <NavLink href="#services" testId="nav-services">
              {t.nav.services}
            </NavLink>
            <NavLink href="#tools" testId="nav-tools">
              {t.nav.tools}
            </NavLink>
            <NavLink href="#case-studies" testId="nav-cases">
              {t.nav.cases}
            </NavLink>
            <NavLink href="#gallery" testId="nav-gallery">
              {t.nav.gallery}
            </NavLink>
            <NavLink href="#faq" testId="nav-faq">
              {t.nav.faq}
            </NavLink>
            <NavLink href="#contact" testId="nav-contact">
              {t.nav.contact}
            </NavLink>
          </nav>

          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="language-switcher-trigger"
                  className="inline-flex items-center gap-2 px-3 py-2 text-xs font-mono-tech uppercase tracking-[0.15em] text-neutral-300 hover:text-white border border-neutral-800 hover:border-neutral-600 transition-colors"
                >
                  <Globe className="h-3.5 w-3.5" />
                  <span>{lang.toUpperCase()}</span>
                  <ChevronDown className="h-3 w-3 opacity-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-neutral-950 border-neutral-800 text-white rounded-none"
              >
                <DropdownMenuLabel className="font-mono-tech text-[10px] tracking-[0.2em] text-neutral-500 uppercase">
                  {t.nav.language}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-neutral-800" />
                <DropdownMenuItem
                  data-testid="lang-option-ro"
                  onClick={() => setLang("ro")}
                  className="font-mono-tech text-xs cursor-pointer focus:bg-yellow-500 focus:text-black"
                >
                  RO · Română {lang === "ro" && "✓"}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="lang-option-en"
                  onClick={() => setLang("en")}
                  className="font-mono-tech text-xs cursor-pointer focus:bg-yellow-500 focus:text-black"
                >
                  EN · English {lang === "en" && "✓"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <a
              href="#contact"
              data-testid="header-cta"
              className="hidden sm:inline-flex items-center px-4 py-2 btn-yellow text-xs font-mono-tech uppercase tracking-[0.15em]"
            >
              {t.nav.cta}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
